import { useState,useEffect,useRef } from 'react';
import MavisQualificationEvidence from './MavisQualificationEvidence';
import { BrainCircuit } from 'lucide-react';
import type { NowToValueData, NowToValueKey, NowToValueField } from '../../types/mavis';
import { NOW_TO_VALUE_KEY_LABELS } from '../../types/mavis';

const NTV_KEYS: NowToValueKey[] = [
  'current_state',
  'negative_consequences',
  'future_state',
  'positive_business_outcomes',
  'required_capabilities',
  'timelines_and_milestones',
  'personas_required',
];

// Contextual placeholder text per field
const PLACEHOLDERS: Record<NowToValueKey, string> = {
  current_state:              'How does the customer operate today? What tools, processes, and pain points exist?',
  negative_consequences:      'What happens if the customer does nothing? What is the cost of inaction?',
  future_state:               'What does success look like after they adopt the solution?',
  positive_business_outcomes: 'What measurable outcomes will the customer achieve?',
  required_capabilities:      'What capabilities or features are essential for success?',
  timelines_and_milestones:   'What are the key dates, deadlines, or decision milestones?',
  personas_required:          'Which stakeholders need to be involved for this to succeed?',
};

const textArea =
  'w-full bg-transparent border-0 focus:outline-none resize-none leading-relaxed placeholder:text-app-muted';

interface Props {
  data: NowToValueData;
  saving: boolean;
  onSave: (updated: NowToValueData) => void;
}

export default function MavisNowToValuePanel({ data, saving: _saving, onSave }: Props) {
  const [draft, setDraft] = useState<NowToValueData>(data);

  const dirty=useRef(new Set<NowToValueKey>());
  useEffect(()=>setDraft(prev=>({...data,...Object.fromEntries([...dirty.current].map(k=>[k,prev[k]]))})),[data]);
  const update = (key: NowToValueKey, patch: Partial<NowToValueField>) => {
    dirty.current.add(key);setDraft(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  };

  const handleBlur = () => {
    if(!dirty.current.size)return;onSave({...data,...Object.fromEntries([...dirty.current].map(k=>[k,draft[k]]))});dirty.current.clear();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {NTV_KEYS.map(k => (
        <div key={k} className="bg-app-surface rounded-2xl border border-app-line shadow-sm">
          {/* Card header */}
          <div className="px-5 py-3 border-b border-app-line">
            <h4 className="text-sm font-semibold text-app-ink">
              {NOW_TO_VALUE_KEY_LABELS[k]}
            </h4>
          </div>

          {/* Card body */}
          <div className="px-5 py-4 space-y-3">
            <MavisQualificationEvidence field={data[k]}/>
            <div>
              <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                What we know
              </p>
              <textarea
                value={draft[k]?.value??''}
                onChange={e => update(k, { value: e.target.value })}
                onBlur={handleBlur}
                rows={4}
                className={`${textArea} text-sm text-app-ink`}
                placeholder={PLACEHOLDERS[k]}
              />
            </div>
            <div className="border-t border-app-line pt-2.5">
              <p className="text-[10px] font-semibold text-app-muted uppercase tracking-wider mb-1">
                Notes
              </p>
              <textarea
                value={draft[k]?.notes??''}
                onChange={e => update(k, { notes: e.target.value })}
                onBlur={handleBlur}
                rows={2}
                className={`${textArea} text-xs text-app-muted`}
                placeholder="Supporting evidence, sources, open questions…"
              />
            </div>

            {/* AI assist placeholder */}
            <button
              disabled
              className="flex items-center gap-1.5 text-[11px] text-app-muted cursor-not-allowed mt-1"
              title="MAVIS AI assist — coming soon"
            >
              <BrainCircuit className="w-3.5 h-3.5" />
              Ask MAVIS
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}
